import Swal from 'sweetalert2';
import type { TFunction } from 'i18next';
import { BinNumberCommand } from '../../../services/command';

const ARRAY_ERROR_KEYS = ['CreditCardTypeId', 'BinNumbers', 'errors'] as const;

const cleanErrorMessagePart = (msg: string): string =>
    msg.split('", "type"')[0].replace(/^"/, '').replace(/"$/, '');

const joinCleanedMessages = (messages: string[]): string =>
    messages.map(cleanErrorMessagePart).join(', ');

const extractQuotedMessages = (raw: string): string => {
    if (!(raw.includes('{') && raw.includes('}'))) {
        return raw;
    }

    const match = raw.match(/"([^"]+)"/g);
    if (!match || match.length === 0) {
        return raw;
    }

    return match
        .map((m) => {
            const cleanMsg = m.replace(/"/g, '');
            return cleanMsg.split('", "type"')[0];
        })
        .join(', ');
};

const messageFromParsedObject = (parsed: any, fallback: string): string => {
    for (const key of ARRAY_ERROR_KEYS) {
        if (Array.isArray(parsed?.[key])) {
            return joinCleanedMessages(parsed[key]);
        }
    }

    if (parsed?.title) {
        return parsed.title;
    }

    if (typeof parsed === 'string') {
        return parsed;
    }

    return fallback;
};

/** API hata mesajını (JSON / dizi / düz metin) kullanıcıya gösterilecek metne çevirir. */
export const parseBinNumberApiErrorMessage = (
    sourceMessage: any,
    fallback = 'Bir hata oluştu'
): string => {
    if (!sourceMessage) {
        return fallback;
    }

    try {
        let messageToProcess = sourceMessage;
        if (typeof messageToProcess === 'object') {
            messageToProcess = JSON.stringify(messageToProcess);
        }

        const parsedMessage = JSON.parse(messageToProcess);
        const fallbackMessage = typeof sourceMessage === 'string' ? sourceMessage : fallback;
        return messageFromParsedObject(parsedMessage, fallbackMessage);
    } catch {
        const asString = typeof sourceMessage === 'string' ? sourceMessage : String(sourceMessage);
        return extractQuotedMessages(asString);
    }
};

const splitBinNumbers = (raw: string): string[] =>
    raw
        .split(',')
        .map((bin) => bin.trim())
        .filter((bin) => bin.length > 0);

const showMissingBinNumbersValidation = (t: TFunction) => {
    Swal.fire({
        icon: 'error',
        title: t('binNumberManagement.validation.missingInfo'),
        text: t('binNumberManagement.validation.pleaseEnterBinNumbers'),
        showConfirmButton: false,
        timer: 2000,
    });
};

const showSaveSuccess = (t: TFunction, successTextKey: string) => {
    Swal.fire({
        icon: 'success',
        title: t('binNumberManagement.messages.success'),
        text: t(successTextKey),
        showConfirmButton: false,
        timer: 2000,
    });
};

const showSaveError = (title: string, text: string, confirmButtonText: string) => {
    Swal.fire({
        icon: 'error',
        title,
        text,
        confirmButtonText,
    });
};

const resolveCaughtErrorMessage = (error: unknown, fallback: string): string => {
    const sourceMessage = (error as any)?.data?.message || (error as any)?.message;
    return parseBinNumberApiErrorMessage(sourceMessage, fallback);
};

export interface DebitBinSaveParams {
    debitBinNumbers: string;
    selectedBankId: string;
    t: TFunction;
    setIsDebitSaving: (value: boolean) => void;
    setDebitBinNumbers: (value: string) => void;
    setIsAddDebitBinModalOpen: (value: boolean) => void;
}

export const saveDebitBinNumbers = async ({
    debitBinNumbers,
    selectedBankId,
    t,
    setIsDebitSaving,
    setDebitBinNumbers,
    setIsAddDebitBinModalOpen,
}: DebitBinSaveParams): Promise<void> => {
    if (!debitBinNumbers.trim()) {
        showMissingBinNumbersValidation(t);
        return;
    }

    try {
        setIsDebitSaving(true);

        const response = await BinNumberCommand.CreateBlackListedCreditCards({
            binNumbers: splitBinNumbers(debitBinNumbers),
            bankId: parseInt(selectedBankId, 10),
            creditCardBlackListReasonId: 1,
        });

        if (response?.statuscode === 200) {
            showSaveSuccess(t, 'binNumberManagement.messages.debitBinAddedSuccess');
            setDebitBinNumbers('');
            setIsAddDebitBinModalOpen(false);
            return;
        }

        const isKnownFailure = Boolean(response?.statuscode && response.statuscode !== 200);
        const errorMessage = parseBinNumberApiErrorMessage(
            response?.message,
            isKnownFailure ? 'Bir hata oluştu' : 'Bilinmeyen hata oluştu'
        );

        showSaveError(
            isKnownFailure ? t('binNumberManagement.messages.operationFailed') : 'İşlem Başarısız',
            errorMessage,
            isKnownFailure ? t('binNumberManagement.buttons.ok') : 'Tamam'
        );
    } catch (error) {
        console.error('Debit Bin Save Error:', error);
        console.error('Error details:', {
            message: (error as any)?.message,
            data: (error as any)?.data,
            response: (error as any)?.response,
        });

        showSaveError(
            t('binNumberManagement.messages.error'),
            resolveCaughtErrorMessage(error, 'Bilinmeyen bir hata oluştu'),
            t('binNumberManagement.buttons.ok')
        );
    } finally {
        setIsDebitSaving(false);
    }
};

export interface CreditBinSaveParams {
    creditBinNumbers: string;
    selectedCreditCardTypeId: string;
    t: TFunction;
    setIsCreditSaving: (value: boolean) => void;
    setCreditBinNumbers: (value: string) => void;
    setIsAddCreditBinModalOpen: (value: boolean) => void;
    refreshCreditCardList: (creditCardTypeId: string) => void;
}

export const saveCreditBinNumbers = async ({
    creditBinNumbers,
    selectedCreditCardTypeId,
    t,
    setIsCreditSaving,
    setCreditBinNumbers,
    setIsAddCreditBinModalOpen,
    refreshCreditCardList,
}: CreditBinSaveParams): Promise<void> => {
    if (!creditBinNumbers.trim()) {
        showMissingBinNumbersValidation(t);
        return;
    }

    try {
        setIsCreditSaving(true);

        const response = await BinNumberCommand.CreateCreditCardTypeBinNumbers({
            binNumbers: splitBinNumbers(creditBinNumbers),
            creditCardTypeId: parseInt(selectedCreditCardTypeId, 10),
        });

        if (response?.statuscode === 200) {
            showSaveSuccess(t, 'binNumberManagement.messages.creditBinAddedSuccess');
            setCreditBinNumbers('');
            setIsAddCreditBinModalOpen(false);
            refreshCreditCardList(selectedCreditCardTypeId);
            return;
        }

        const isKnownFailure = Boolean(response?.statuscode && response.statuscode !== 200);
        const errorMessage = parseBinNumberApiErrorMessage(
            response?.message,
            isKnownFailure ? 'Bir hata oluştu' : 'Bilinmeyen hata oluştu'
        );

        showSaveError(
            t('binNumberManagement.messages.operationFailed'),
            errorMessage,
            t('binNumberManagement.buttons.ok')
        );
    } catch (error) {
        console.error('Credit Bin Save Error:', error);
        console.error('Error details:', {
            message: (error as any)?.message,
            data: (error as any)?.data,
            response: (error as any)?.response,
        });

        showSaveError(
            t('binNumberManagement.messages.error'),
            resolveCaughtErrorMessage(error, 'Bilinmeyen bir hata oluştu'),
            t('binNumberManagement.buttons.ok')
        );
    } finally {
        setIsCreditSaving(false);
    }
};
